import type { FluidPropertiesContext } from "../fluidProperties/d";
import { Rankine } from "@/libs/units/temperature";
import type { PipeFrictionContext } from "./d";

/**
 * Computes friction properties for a pipe segment.
 *
 * @param referencePointPressurePSIA - Pressure at the inlet/start of the pipe segment (e.g., well pressure).
 *                                     For tubing calculations, this is typically 0.
 * @param backpressurePSIA - Additional system backpressure (e.g., discharge or downstream resistance).
 *                           Typically 0 for standard tubing. Default is 0.
 */
export function getPipeFrictionContext(
  phiRad: number,
  surfaceTemperatureF: number,
  bottomholeTemperatureF: number,
  pipeLengthFt: number,
  pipeDiameterIn: number,
  pipeDepthFt: number,
  fluidPropertiesContext: FluidPropertiesContext,
  pipeRoughnessIn: number,
  referencePointPressurePSIA: number,
  palmerCorrection: boolean = false,
  backpressurePSIA: number = 0,
  griffithWallis: boolean = false
): PipeFrictionContext {
  const flowRatesAtSurface = fluidPropertiesContext.flowRatesAtSurface;
  const flowRatesInSitu = fluidPropertiesContext.flowRatesInSitu;
  if (!flowRatesInSitu) throw new Error("FlowRatesInSitu is required");

  const { waterCutRatio } = flowRatesAtSurface;
  const {
    fluidProperties: { gasSuperficialVelocityFTSEC, liquidDensityLBMFT3 },
    fluidFrictionProperties: { liquidInterfacialTension }
  } = fluidPropertiesContext;

  const waterCutPercentage = waterCutRatio || 0;

  const T = (surfaceTemperatureF + bottomholeTemperatureF) / 2;
  const T_rankine = Rankine.toThisUnit(T);

  const P = referencePointPressurePSIA;

  const pipeRoughnessInVal = pipeRoughnessIn || 0;

  const epsilonD = pipeRoughnessInVal / pipeDiameterIn;

  // D307: =(D149*(D132/12)^2)/((D132/12)-D5)^2
  const VSG =
    (gasSuperficialVelocityFTSEC * (pipeDiameterIn / 12) ** 2) /
    (pipeDiameterIn / 12 - pipeRoughnessInVal) ** 2;

  // N_D
  // D200: =120.872*(D132/12)*SQRT(D63/D168)
  const pipeDiameterNumber =
    120.872 *
    (pipeDiameterIn / 12) *
    Math.sqrt(liquidDensityLBMFT3 / liquidInterfacialTension);

  return {
    T,
    T_rankine,
    P,
    referencePointPressurePSIA,
    phiRad,
    waterCutPercentage,
    pipeRoughnessIn: pipeRoughnessInVal,
    pipeLengthFt,
    pipeDiameterIn,
    pipeDepthFt,
    epsilonD,
    VSG,
    pipeDiameterNumber,
    palmerCorrection,
    backpressurePSIA,
    griffithWallis,
    fluidPropertiesContext
  };
}
